import dayjs from 'dayjs'
import {
  AdGroupBudgetPlanCreateInput,
  AdGroupBudgetPlanPartsFragment,
  AdGroupBudgetPlanUpdateInput,
  BudgetCapping,
  CostType,
  DailyBudgetType,
} from '../../../../../__generated__/graphql'

export type BudgetPlanFormValues = {
  plannedDate: string | null
  dailyBudgetType: DailyBudgetType | ''
  dailyBudget: number | '' | null
  budgetCappingType: BudgetCapping | ''
  budgetCapping: number | '' | null
  costType: CostType | ''
  costValue: number | '' | null
  startDate: string | null
  endDate: string | null
}

export const emptyBudgetPlanFormValues = (): BudgetPlanFormValues => ({
  plannedDate: null,
  dailyBudgetType: '',
  dailyBudget: '',
  budgetCappingType: '',
  budgetCapping: '',
  costType: '',
  costValue: '',
  startDate: null,
  endDate: null,
})

export const toUtcDateOnly = (value: unknown): string | null => {
  if (value == null || value === '') return null
  if (typeof value === 'string') {
    const dateOnlyMatch = value.match(/^(\d{4}-\d{2}-\d{2})/)
    if (dateOnlyMatch) return dateOnlyMatch[1]
  }
  const parsed = dayjs(value as string | Date)
  if (!parsed.isValid()) return null
  return parsed.format('YYYY-MM-DD')
}

/** Formats plannedDate in UTC as `DD/MM/YYYY HH:mm` so local TZ does not shift the day. */
export const formatBudgetPlanUtcDateTime = (plannedDate: unknown): string | null => {
  if (plannedDate == null || plannedDate === '') return null

  if (typeof plannedDate === 'string') {
    const utcMatch = plannedDate.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/)
    if (utcMatch) {
      const [, year, month, day, hour = '00', minute = '00'] = utcMatch
      return `${day}/${month}/${year} ${hour}:${minute}`
    }
  }

  const date = plannedDate instanceof Date ? plannedDate : new Date(plannedDate as string)
  if (Number.isNaN(date.getTime())) return null

  const day = String(date.getUTCDate()).padStart(2, '0')
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const year = date.getUTCFullYear()
  const hour = String(date.getUTCHours()).padStart(2, '0')
  const minute = String(date.getUTCMinutes()).padStart(2, '0')
  return `${day}/${month}/${year} ${hour}:${minute}`
}

export const todayUtcDateOnly = (): string => new Date().toISOString().slice(0, 10)

export const tomorrowUtcDateOnly = (): string => {
  const [year, month, day] = todayUtcDateOnly().split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day + 1)).toISOString().slice(0, 10)
}

export const isFuturePlannedDate = (plannedDate: unknown, todayUtc: string = todayUtcDateOnly()): boolean => {
  const dateOnly = toUtcDateOnly(plannedDate)
  return dateOnly != null && dateOnly > todayUtc
}

const compareBudgetPlansByPlannedDate = (
  a: AdGroupBudgetPlanPartsFragment,
  b: AdGroupBudgetPlanPartsFragment
): number => {
  const left = toUtcDateOnly(a.plannedDate) ?? ''
  const right = toUtcDateOnly(b.plannedDate) ?? ''
  return left.localeCompare(right)
}

export const filterFutureBudgetPlans = (
  plans: AdGroupBudgetPlanPartsFragment[],
  todayUtc: string = todayUtcDateOnly()
): AdGroupBudgetPlanPartsFragment[] =>
  plans.filter((plan) => isFuturePlannedDate(plan.plannedDate, todayUtc)).sort(compareBudgetPlansByPlannedDate)

export const sortBudgetPlansByPlannedDate = (
  plans: AdGroupBudgetPlanPartsFragment[]
): AdGroupBudgetPlanPartsFragment[] => [...plans].sort(compareBudgetPlansByPlannedDate)

export const existingPlanDateKeys = (plans: AdGroupBudgetPlanPartsFragment[]): string[] =>
  plans.map((plan) => toUtcDateOnly(plan.plannedDate)).filter((date): date is string => date != null)

const hasNumericValue = (value: number | '' | null | undefined): value is number =>
  value != null && value !== '' && !Number.isNaN(Number(value))

const hasDateValue = (value: string | null | undefined): value is string =>
  value != null && value !== '' && toUtcDateOnly(value) != null

export const hasBudgetPlanChange = (values: BudgetPlanFormValues): boolean =>
  Boolean(values.dailyBudgetType) ||
  hasNumericValue(values.dailyBudget) ||
  Boolean(values.budgetCappingType) ||
  hasNumericValue(values.budgetCapping) ||
  Boolean(values.costType) ||
  hasNumericValue(values.costValue) ||
  hasDateValue(values.startDate) ||
  hasDateValue(values.endDate)

export type BudgetPlanAdGroupDefaults = {
  costType?: CostType | null
  costValue?: number | null
}

type BudgetPlanSharedInput = Omit<AdGroupBudgetPlanCreateInput, 'adGroupId'>

const toBudgetPlanSharedInput = (
  values: BudgetPlanFormValues,
  adgroup: BudgetPlanAdGroupDefaults = {}
): BudgetPlanSharedInput => {
  const plannedDate = toUtcDateOnly(values.plannedDate)
  if (!plannedDate) {
    throw new Error('plannedDate is required')
  }

  const changeDailyBudget = Boolean(values.dailyBudgetType) || hasNumericValue(values.dailyBudget)
  const changeBudgetCapping = Boolean(values.budgetCappingType) || hasNumericValue(values.budgetCapping)
  const changeCost = Boolean(values.costType) || hasNumericValue(values.costValue)
  const changeDates = hasDateValue(values.startDate) || hasDateValue(values.endDate)

  const input: BudgetPlanSharedInput = {
    plannedDate,
    changeDailyBudget,
    changeBudgetCapping,
    changeCost,
    changeDates,
  }

  if (values.dailyBudgetType) {
    input.dailyBudgetType = values.dailyBudgetType
  }

  const shouldSendDailyBudget =
    values.dailyBudgetType === DailyBudgetType.Fixed || (!values.dailyBudgetType && hasNumericValue(values.dailyBudget))

  if (shouldSendDailyBudget && hasNumericValue(values.dailyBudget)) {
    input.dailyBudget = Number(values.dailyBudget)
  }

  if (values.budgetCappingType) {
    input.budgetCappingType = values.budgetCappingType
  }

  const shouldSendBudgetCapping =
    (Boolean(values.budgetCappingType) && values.budgetCappingType !== BudgetCapping.Open) ||
    (!values.budgetCappingType && hasNumericValue(values.budgetCapping))

  if (shouldSendBudgetCapping && hasNumericValue(values.budgetCapping)) {
    input.budgetCapping = Number(values.budgetCapping)
  }

  if (changeCost) {
    const costType = values.costType || (adgroup.costType as CostType | undefined)
    if (costType) {
      input.costType = costType
    }

    if (hasNumericValue(values.costValue)) {
      input.costValue = Number(values.costValue)
    } else if (typeof adgroup.costValue === 'number' && !Number.isNaN(adgroup.costValue)) {
      input.costValue = adgroup.costValue
    }
  }

  if (changeDates) {
    input.startDate = toUtcDateOnly(values.startDate)
    input.endDate = toUtcDateOnly(values.endDate)
  }

  return input
}

export const toAdGroupBudgetPlanCreateInput = (
  adGroupId: string,
  values: BudgetPlanFormValues,
  adgroup: BudgetPlanAdGroupDefaults = {}
): AdGroupBudgetPlanCreateInput => ({
  adGroupId,
  ...toBudgetPlanSharedInput(values, adgroup),
})

export const toBudgetPlanFormValues = (plan: AdGroupBudgetPlanPartsFragment): BudgetPlanFormValues => ({
  plannedDate: toUtcDateOnly(plan.plannedDate),
  dailyBudgetType: plan.changeDailyBudget ? (plan.dailyBudgetType ?? '') : '',
  dailyBudget: plan.changeDailyBudget && plan.dailyBudget != null ? plan.dailyBudget : '',
  budgetCappingType: plan.changeBudgetCapping ? (plan.budgetCappingType ?? '') : '',
  budgetCapping: plan.changeBudgetCapping && plan.budgetCapping != null ? plan.budgetCapping : '',
  costType: plan.changeCost ? (plan.costType ?? '') : '',
  costValue: plan.changeCost && plan.costValue != null ? plan.costValue : '',
  startDate: plan.changeDates ? toUtcDateOnly(plan.startDate) : null,
  endDate: plan.changeDates ? toUtcDateOnly(plan.endDate) : null,
})

export const toAdGroupBudgetPlanUpdateInput = (
  id: string,
  values: BudgetPlanFormValues,
  adgroup: BudgetPlanAdGroupDefaults = {}
): AdGroupBudgetPlanUpdateInput => ({
  id,
  ...toBudgetPlanSharedInput(values, adgroup),
})
